import { useMemo, useState } from 'react'
import { CurrencyInput } from '@/components/CurrencyInput'
import type { AdminCampaignListItem, OperatingCostCategory, OperatingCostPixFee } from '@/types/api'
import {
  displayOperatingCostCategoryName,
  formatCategoryTotal,
  formatOperatingCostAmount,
  formatOperatingCostRate,
  formatRateInputValue,
  parseOptionalAmountInput,
  parseRateInput,
} from '../lib/operating-costs'
import { usePixFees } from '../hooks/usePixFees'
import styles from '../CustosOperacionaisPage.module.css'
import { OperatingCostCampaignPicker } from './OperatingCostCampaignPicker'
import { PaymentFeePostingsSection } from './PaymentFeePostingsSection'

type PixFeePanelProps = {
  category: OperatingCostCategory
  onSaved?: () => Promise<void> | void
}

export function PixFeePanel({ category, onSaved }: PixFeePanelProps) {
  const [postingsEpoch, setPostingsEpoch] = useState(0)
  const fees = usePixFees({
    onSaved: async () => {
      setPostingsEpoch((current) => current + 1)
      await onSaved?.()
    },
  })
  const [editingCampaignId, setEditingCampaignId] = useState<number | null>(null)
  const [selectedCampaign, setSelectedCampaign] = useState<AdminCampaignListItem | null>(null)
  const [campaignRate, setCampaignRate] = useState('')
  const [campaignFixedAmount, setCampaignFixedAmount] = useState('')
  const [campaignFormError, setCampaignFormError] = useState<string | null>(null)
  const [pendingCampaignDelete, setPendingCampaignDelete] = useState<number | null>(null)

  const defaultFee = fees.data?.default ?? null
  const campaigns = fees.data?.campaigns
  const excludeIds = useMemo(
    () => (campaigns ?? []).map((item) => item.campaign_id).filter((id): id is number => id !== null),
    [campaigns]
  )

  function startCampaignEdit(campaignId: number, currentRate: number, currentFixed: number) {
    setSelectedCampaign(null)
    setEditingCampaignId(campaignId)
    setCampaignRate(formatRateInputValue(currentRate))
    setCampaignFixedAmount(currentFixed === 0 ? '' : String(currentFixed))
    setCampaignFormError(null)
  }

  function submitCampaign(campaignId: number) {
    const parsedRate = parseRateInput(campaignRate)
    if (parsedRate === null) {
      setCampaignFormError('Informe o percentual da taxa PIX.')
      return
    }
    const parsedFixed = parseOptionalAmountInput(campaignFixedAmount)
    if (parsedFixed === null) {
      setCampaignFormError('Informe um valor fixo válido.')
      return
    }
    setCampaignFormError(null)
    void fees.saveCampaign(campaignId, { rate: parsedRate, fixedAmount: parsedFixed }).then((ok) => {
      if (ok) {
        setEditingCampaignId(null)
        setSelectedCampaign(null)
      }
    })
  }

  return (
    <article className={styles.categoryCard} aria-labelledby={`operating-cost-category-${category.id}`}>
      <header className={styles.categoryHeader}>
        <div className={styles.categoryHeading}>
          <h3 id={`operating-cost-category-${category.id}`} className={styles.categoryTitle}>
            {displayOperatingCostCategoryName(category)}
          </h3>
          <span className={styles.badge}>Padrão</span>
        </div>
        <p className={styles.categoryTotal}>{formatCategoryTotal(category)}</p>
      </header>

      {fees.error ? (
        <p className={styles.inlineError} role="alert">
          {fees.error}
        </p>
      ) : null}
      {fees.success ? (
        <p className={styles.bannerSuccess} role="status">
          {fees.success}
        </p>
      ) : null}

      <section className={`${styles.feeSection} ${styles.feeSectionHighlighted}`} aria-labelledby={`pix-default-${category.id}`}>
        <div className={styles.sectionHeading}>
          <h4 id={`pix-default-${category.id}`} className={styles.sectionTitle}>
            Valores padrão
          </h4>
          <span className={styles.badge}>Padrão — todas as campanhas</span>
        </div>
        <p className={styles.othersCopy}>
          Vale para todas as campanhas, exceto as personalizadas abaixo. A taxa PIX é obrigatória. O
          valor fixo em reais é opcional.
        </p>
        {fees.isLoading && fees.data === null ? (
          <p className={styles.empty}>Carregando taxa padrão...</p>
        ) : (
          <PixDefaultForm
            key={
              defaultFee
                ? `pix-default-${defaultFee.rate}-${defaultFee.fixed_amount}`
                : 'pix-default-empty'
            }
            categoryId={category.id}
            defaultFee={defaultFee}
            isSaving={fees.isSaving}
            onSave={(input) => {
              void fees.saveDefault(input)
            }}
            onDelete={() => fees.deleteDefault()}
          />
        )}
      </section>

      <section className={styles.feeSection} aria-labelledby={`pix-custom-${category.id}`}>
        <h4 id={`pix-custom-${category.id}`} className={styles.sectionTitle}>
          Valores personalizados por campanha
        </h4>
        <p className={styles.othersCopy}>
          A campanha personalizada substitui por completo o percentual e o valor fixo padrão.
        </p>

        <OperatingCostCampaignPicker
          excludeIds={excludeIds}
          disabled={fees.isSaving}
          onSelect={(campaign) => {
            setSelectedCampaign(campaign)
            setEditingCampaignId(null)
            setCampaignRate(defaultFee ? formatRateInputValue(defaultFee.rate) : '')
            setCampaignFixedAmount(
              defaultFee && defaultFee.fixed_amount !== 0 ? String(defaultFee.fixed_amount) : ''
            )
            setCampaignFormError(null)
          }}
        />

        {selectedCampaign ? (
          <form
            className={styles.feeForm}
            onSubmit={(event) => {
              event.preventDefault()
              submitCampaign(selectedCampaign.id)
            }}
          >
            <p className={styles.othersCopy}>
              Nova taxa para {selectedCampaign.name}
              {selectedCampaign.institution_name ? ` · ${selectedCampaign.institution_name}` : ''}.
            </p>
            <label className={styles.field} htmlFor={`pix-new-campaign-rate-${category.id}`}>
              Taxa PIX (%)
              <input
                id={`pix-new-campaign-rate-${category.id}`}
                className={styles.input}
                inputMode="decimal"
                value={campaignRate}
                disabled={fees.isSaving}
                onChange={(event) => setCampaignRate(event.target.value)}
              />
            </label>
            <label className={styles.field} htmlFor={`pix-new-campaign-fixed-${category.id}`}>
              Valor fixo (R$)
              <CurrencyInput
                id={`pix-new-campaign-fixed-${category.id}`}
                value={campaignFixedAmount}
                disabled={fees.isSaving}
                onChange={setCampaignFixedAmount}
              />
            </label>
            <div className={styles.filterActions}>
              <button type="submit" className={styles.primaryButton} disabled={fees.isSaving}>
                {fees.isSaving ? 'Salvando...' : 'Salvar campanha'}
              </button>
              <button
                type="button"
                className={styles.ghostButton}
                disabled={fees.isSaving}
                onClick={() => setSelectedCampaign(null)}
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : null}

        {campaigns && campaigns.length > 0 ? (
          <ul className={styles.campaignFeeList}>
            {campaigns.map((item) => {
              const campaignId = item.campaign_id
              if (campaignId === null) return null
              const isEditing = editingCampaignId === campaignId
              return (
                <li key={campaignId} className={styles.campaignFeeItem}>
                  <div className={styles.campaignFeeHeading}>
                    <strong>{item.campaign_name}</strong>
                    <span className={styles.campaignResultMeta}>{item.institution_name}</span>
                    <span>
                      {formatOperatingCostRate(item.rate)}
                      {item.fixed_amount > 0 ? ` + ${formatOperatingCostAmount(item.fixed_amount)}` : ''}
                    </span>
                  </div>
                  {isEditing ? (
                    <form
                      className={styles.feeForm}
                      onSubmit={(event) => {
                        event.preventDefault()
                        submitCampaign(campaignId)
                      }}
                    >
                      <label className={styles.field} htmlFor={`pix-edit-rate-${campaignId}`}>
                        Taxa PIX (%)
                        <input
                          id={`pix-edit-rate-${campaignId}`}
                          className={styles.input}
                          inputMode="decimal"
                          value={campaignRate}
                          disabled={fees.isSaving}
                          onChange={(event) => setCampaignRate(event.target.value)}
                        />
                      </label>
                      <label className={styles.field} htmlFor={`pix-edit-fixed-${campaignId}`}>
                        Valor fixo (R$)
                        <CurrencyInput
                          id={`pix-edit-fixed-${campaignId}`}
                          value={campaignFixedAmount}
                          disabled={fees.isSaving}
                          onChange={setCampaignFixedAmount}
                        />
                      </label>
                      <div className={styles.filterActions}>
                        <button type="submit" className={styles.primaryButton} disabled={fees.isSaving}>
                          {fees.isSaving ? 'Salvando...' : 'Salvar campanha'}
                        </button>
                        <button
                          type="button"
                          className={styles.ghostButton}
                          disabled={fees.isSaving}
                          onClick={() => setEditingCampaignId(null)}
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className={styles.filterActions}>
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        disabled={fees.isSaving}
                        onClick={() => startCampaignEdit(campaignId, item.rate, item.fixed_amount)}
                      >
                        Editar
                      </button>
                      {pendingCampaignDelete === campaignId ? (
                        <>
                          <button
                            type="button"
                            className={styles.dangerButton}
                            disabled={fees.isSaving}
                            onClick={() => {
                              void fees.deleteCampaign(campaignId).then((ok) => {
                                if (ok) setPendingCampaignDelete(null)
                              })
                            }}
                          >
                            Confirmar exclusão
                          </button>
                          <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => setPendingCampaignDelete(null)}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={styles.dangerButton}
                          disabled={fees.isSaving}
                          onClick={() => setPendingCampaignDelete(campaignId)}
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className={styles.empty}>Nenhuma campanha personalizada ainda.</p>
        )}
        {campaignFormError ? (
          <p className={styles.inlineError} role="alert">
            {campaignFormError}
          </p>
        ) : null}
      </section>

      <PaymentFeePostingsSection
        categoryId={category.id}
        reloadToken={postingsEpoch}
        emptyMessage="Nenhuma venda PIX paga neste filtro."
      />
    </article>
  )
}

type PixDefaultFormProps = {
  categoryId: number
  defaultFee: OperatingCostPixFee | null
  isSaving: boolean
  onSave: (input: { rate: number; fixedAmount: number }) => void
  onDelete: () => Promise<boolean>
}

function PixDefaultForm({
  categoryId,
  defaultFee,
  isSaving,
  onSave,
  onDelete,
}: PixDefaultFormProps) {
  const [rate, setRate] = useState(defaultFee ? formatRateInputValue(defaultFee.rate) : '')
  const [fixedAmount, setFixedAmount] = useState(
    defaultFee && defaultFee.fixed_amount !== 0 ? String(defaultFee.fixed_amount) : ''
  )
  const [formError, setFormError] = useState<string | null>(null)
  const [pendingDefaultDelete, setPendingDefaultDelete] = useState(false)

  function submitDefault() {
    const parsedRate = parseRateInput(rate)
    if (parsedRate === null) {
      setFormError('Informe o percentual da taxa PIX.')
      return
    }
    const parsedFixed = parseOptionalAmountInput(fixedAmount)
    if (parsedFixed === null) {
      setFormError('Informe um valor fixo válido.')
      return
    }
    setFormError(null)
    onSave({ rate: parsedRate, fixedAmount: parsedFixed })
  }

  return (
    <>
      <form
        className={styles.feeForm}
        onSubmit={(event) => {
          event.preventDefault()
          submitDefault()
        }}
      >
        <label className={styles.field} htmlFor={`pix-default-rate-${categoryId}`}>
          Taxa PIX (%)
          <input
            id={`pix-default-rate-${categoryId}`}
            className={styles.input}
            inputMode="decimal"
            value={rate}
            disabled={isSaving}
            onChange={(event) => setRate(event.target.value)}
          />
        </label>
        <label className={styles.field} htmlFor={`pix-default-fixed-${categoryId}`}>
          Valor fixo (R$)
          <CurrencyInput
            id={`pix-default-fixed-${categoryId}`}
            value={fixedAmount}
            disabled={isSaving}
            onChange={setFixedAmount}
          />
        </label>
        <div className={styles.filterActions}>
          <button type="submit" className={styles.primaryButton} disabled={isSaving}>
            {isSaving ? 'Salvando...' : 'Salvar padrão'}
          </button>
          {defaultFee ? (
            pendingDefaultDelete ? (
              <>
                <button
                  type="button"
                  className={styles.dangerButton}
                  disabled={isSaving}
                  onClick={() => {
                    void onDelete().then((ok) => {
                      if (ok) setPendingDefaultDelete(false)
                    })
                  }}
                >
                  Confirmar exclusão
                </button>
                <button
                  type="button"
                  className={styles.ghostButton}
                  disabled={isSaving}
                  onClick={() => setPendingDefaultDelete(false)}
                >
                  Cancelar
                </button>
              </>
            ) : (
              <button
                type="button"
                className={styles.dangerButton}
                disabled={isSaving}
                onClick={() => setPendingDefaultDelete(true)}
              >
                Excluir padrão
              </button>
            )
          ) : null}
        </div>
      </form>
      {formError ? (
        <p className={styles.inlineError} role="alert">
          {formError}
        </p>
      ) : null}
    </>
  )
}
